import { useEffect, useRef, useState } from 'react'
import { API, SHOP_URL } from './api'
import { request } from './StockAdmin'

/** Režim údržby e-shopu: prepínač, texty a fotka stránky „Pripravujeme…“, odkaz na náhľad a odkazy zákazníkov. */

export const MAINTENANCE_PAGE = 'Režim údržby'

type Settings = { enabled: boolean; eyebrow: string; title: string; text: string; imageUrl: string; showForm: boolean; showContacts: boolean; showStores: boolean; recipient: string; thanks: string; previewToken: string; enabledAt: string | null }
type Message = { id: string; name: string; email: string; phone: string | null; message: string; emailed: boolean; readAt: string | null; createdAt: string }

const imageSrc = (url: string) => url.startsWith('/uploads/') ? `${API}${url}` : url.startsWith('/images/') ? `${SHOP_URL}${url}` : url
const when = (iso: string) => new Date(iso).toLocaleString('sk-SK', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })

/** Odznak v hornej lište – vidno ho na každej stránke administrácie, keď je údržba zapnutá. */
export function MaintenanceBadge({ onOpen, refreshKey }: { onOpen: () => void; refreshKey: number }) {
  const [state, setState] = useState<{ enabled: boolean; unread: number } | null>(null)
  useEffect(() => {
    let alive = true
    const load = () => request('/api/admin/maintenance').then(d => { if (alive) setState({ enabled: d.settings.enabled, unread: d.unread }) }).catch(() => undefined)
    void load()
    const timer = setInterval(load, 60000)
    return () => { alive = false; clearInterval(timer) }
  }, [refreshKey])
  if (!state || (!state.enabled && !state.unread)) return null
  return <button type="button" className={`mt-badge-top ${state.enabled ? 'is-on' : ''}`} onClick={onOpen} title="Režim údržby a odkazy zákazníkov">
    {state.enabled && <><span className="mt-badge-dot" />Údržba zapnutá</>}
    {state.unread > 0 && <span className="mt-badge-count">{state.unread} {state.unread === 1 ? 'nový odkaz' : state.unread < 5 ? 'nové odkazy' : 'nových odkazov'}</span>}
  </button>
}

export default function MaintenanceAdmin({ onChanged }: { onChanged: () => void }) {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [saved, setSaved] = useState<Settings | null>(null)
  const [defaults, setDefaults] = useState<Partial<Settings>>({})
  const [mailConfigured, setMailConfigured] = useState(true)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [open, setOpen] = useState<string | null>(null)
  const file = useRef<HTMLInputElement>(null)

  const load = () => request('/api/admin/maintenance').then(d => { setSettings(d.settings); setSaved(d.settings); setDefaults(d.defaults); setMailConfigured(d.mailConfigured) }).catch(e => setError(e.message))
  const loadMessages = () => request('/api/admin/contact-messages').then(d => setMessages(d.messages ?? [])).catch(() => undefined)
  useEffect(() => { void load(); void loadMessages() }, [])

  const dirty = settings && saved && JSON.stringify({ ...settings, enabled: false }) !== JSON.stringify({ ...saved, enabled: false })
  const set = (patch: Partial<Settings>) => { setSettings(s => s ? { ...s, ...patch } : s); setNotice('') }
  const body = (s: Settings) => { const { previewToken: _t, enabledAt: _e, ...rest } = s; return rest }

  async function save(next = settings, message = 'Uložené.') {
    if (!next) return
    setBusy('save'); setError('')
    try { const d = await request('/api/admin/maintenance', { method: 'PUT', body: JSON.stringify(body(next)) }); setSettings(d.settings); setSaved(d.settings); setNotice(message); onChanged() }
    catch (e) { setError(e instanceof Error ? e.message : 'Uloženie zlyhalo.') } finally { setBusy('') }
  }
  async function toggle() {
    if (!settings) return
    const enabled = !settings.enabled
    if (!enabled && !window.confirm('Vypnúť režim údržby? Zákazníci uvidia e-shop a budú môcť objednávať.')) return
    await save({ ...settings, enabled }, enabled ? 'Režim údržby je zapnutý – zákazníci vidia stránku „Pripravujeme“.' : 'E-shop je zverejnený pre zákazníkov.')
  }
  async function upload(f: File) {
    const data = new FormData(); data.append('image', f)
    setBusy('upload'); setError('')
    try {
      const response = await fetch(`${API}/api/admin/site/images`, { method: 'POST', body: data, credentials: 'include' })
      const json = await response.json().catch(() => null)
      if (!response.ok) throw new Error(json?.error || 'Nahratie obrázka zlyhalo.')
      set({ imageUrl: json.url })
    } catch (e) { setError(e instanceof Error ? e.message : 'Nahratie zlyhalo.') } finally { setBusy(''); if (file.current) file.current.value = '' }
  }
  async function newLink() {
    if (!window.confirm('Vytvoriť nový odkaz na náhľad? Starý odkaz prestane fungovať.')) return
    try { const d = await request('/api/admin/maintenance/new-preview-link', { method: 'POST', body: '{}' }); setSettings(s => s ? { ...s, previewToken: d.settings.previewToken } : s); setNotice('Nový odkaz na náhľad je pripravený.') } catch (e) { setError(e instanceof Error ? e.message : 'Nepodarilo sa.') }
  }
  async function markRead(m: Message, read: boolean) {
    await request(`/api/admin/contact-messages/${m.id}`, { method: 'PATCH', body: JSON.stringify({ read }) }).catch(() => undefined)
    void loadMessages(); onChanged()
  }
  async function remove(m: Message) {
    if (!window.confirm(`Vymazať odkaz od ${m.name}?`)) return
    await request(`/api/admin/contact-messages/${m.id}`, { method: 'DELETE' }).catch(() => undefined)
    void loadMessages(); onChanged()
  }
  const expand = (m: Message) => { setOpen(o => o === m.id ? null : m.id); if (!m.readAt) void markRead(m, true) }

  if (!settings) return <div className="panel"><p className="empty-message">{error || 'Načítavam…'}</p></div>
  const previewUrl = `${SHOP_URL}/?nahlad-eshopu=${encodeURIComponent(settings.previewToken)}`
  const unread = messages.filter(m => !m.readAt).length

  return <div className="mt-admin">
    <section className={`mt-hero ${settings.enabled ? 'is-on' : ''}`}>
      <div className="mt-hero-icon" aria-hidden="true">{settings.enabled ? '🛠' : '🛍'}</div>
      <div className="mt-hero-text">
        <h2>{settings.enabled ? 'E-shop je v režime údržby' : 'E-shop je zverejnený'}</h2>
        <p>{settings.enabled
          ? <>Zákazníci vidia iba stránku „{saved?.title}“{settings.enabledAt ? ` (od ${when(settings.enabledAt)})` : ''}. Objednávky a registrácie sú vypnuté. Administrácia funguje normálne.</>
          : 'Zákazníci vidia e-shop a môžu objednávať. Keď budete niečo väčšie prerábať, zapnite režim údržby.'}</p>
      </div>
      <button type="button" role="switch" aria-checked={settings.enabled} className={`mt-switch ${settings.enabled ? 'is-on' : ''}`} disabled={!!busy} onClick={() => void toggle()}>
        <span className="mt-switch-track"><span className="mt-switch-knob" /></span>
        <b>{settings.enabled ? 'Zapnutý' : 'Vypnutý'}</b>
      </button>
    </section>

    <section className="panel mt-preview-link">
      <div><strong>Náhľad e-shopu počas údržby</strong><p>Tento odkaz otvorí normálny e-shop iba vo vašom prehliadači (zapamätá sa, kým nekliknete „Ukončiť náhľad“ alebo „Ako to vidia zákazníci“). Odkaz nikomu neposielajte.</p></div>
      <div className="mt-link-actions">
        <a className="primary-btn" href={previewUrl} target="_blank" rel="noopener">Otvoriť náhľad e-shopu ↗</a>
        <a className="pa-ghost" href={`${SHOP_URL}/?ako-zakaznik=1`} target="_blank" rel="noopener">Ako to vidia zákazníci ↗</a>
        <button type="button" className="pa-link" onClick={() => void newLink()}>Nový odkaz</button>
      </div>
    </section>

    <div className="mt-grid">
      <section className="panel mt-form-panel">
        <div className="pa-head"><div><h2>Stránka „Pripravujeme“</h2><p>Čo uvidia zákazníci, kým je údržba zapnutá.</p></div></div>
        <div className="mt-fields">
          <label className="sk-field">Malý nadpis nad textom<input maxLength={60} value={settings.eyebrow} onChange={e => set({ eyebrow: e.target.value })} /></label>
          <label className="sk-field">Nadpis<input maxLength={120} value={settings.title} onChange={e => set({ title: e.target.value })} /></label>
          <label className="sk-field">Text<textarea rows={4} maxLength={1200} value={settings.text} onChange={e => set({ text: e.target.value })} /></label>
          <div className="sk-field">Fotka cukrárne
            <div className="mt-image-pick">
              {settings.imageUrl ? <img src={imageSrc(settings.imageUrl)} alt="" /> : <span className="mt-noimg">bez fotky</span>}
              <div>
                <button type="button" className="pa-ghost" disabled={busy === 'upload'} onClick={() => file.current?.click()}>{busy === 'upload' ? 'Nahrávam…' : 'Nahrať fotku'}</button>
                {defaults.imageUrl && settings.imageUrl !== defaults.imageUrl && <button type="button" className="pa-link" onClick={() => set({ imageUrl: defaults.imageUrl! })}>Predvolená fotka</button>}
                <small>JPG, PNG alebo WebP do 6 MB, ideálne na výšku alebo štvorcová.</small>
              </div>
              <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={e => { const f = e.target.files?.[0]; if (f) void upload(f) }} />
            </div>
          </div>
          <div className="mt-checks">
            <label className="sa-check"><input type="checkbox" checked={settings.showForm} onChange={e => set({ showForm: e.target.checked })} /> Formulár „Nechajte nám odkaz“</label>
            <label className="sa-check"><input type="checkbox" checked={settings.showContacts} onChange={e => set({ showContacts: e.target.checked })} /> Telefón a e-mail</label>
            <label className="sa-check"><input type="checkbox" checked={settings.showStores} onChange={e => set({ showStores: e.target.checked })} /> Naše cukrárne s dnešnými otváracími hodinami</label>
          </div>
          {settings.showForm && <>
            <label className="sk-field">Odkazy posielať na e-mail<input type="email" maxLength={150} value={settings.recipient} onChange={e => set({ recipient: e.target.value })} /></label>
            {!mailConfigured && <p className="mt-warn">Odosielanie e-mailov zo servera ešte nie je nastavené – odkazy sa zatiaľ uložia iba sem do administrácie.</p>}
            <label className="sk-field">Poďakovanie po odoslaní<input maxLength={300} value={settings.thanks} onChange={e => set({ thanks: e.target.value })} /></label>
          </>}
        </div>
        {error && <p className="error-message" role="alert">{error}</p>}
        <div className="mt-save">
          {notice && <span className="mt-notice">{notice}</span>}
          <button type="button" className="pa-ghost" disabled={!dirty || !!busy} onClick={() => { if (saved) setSettings({ ...saved }) }}>Zrušiť zmeny</button>
          <button type="button" className="primary-btn" disabled={!dirty || !!busy} onClick={() => void save()}>{busy === 'save' ? 'Ukladám…' : 'Uložiť stránku'}</button>
        </div>
      </section>

      <section className="mt-mock-wrap" aria-label="Náhľad stránky">
        <div className="mt-mock-bar"><span /><span /><span /><em>{SHOP_URL.replace(/^https?:\/\//, '')}</em></div>
        <div className="mt-mock">
          <div className="mt-mock-img" style={{ backgroundImage: settings.imageUrl ? `url("${imageSrc(settings.imageUrl)}")` : undefined }}><i>● Pripravujeme</i></div>
          <div className="mt-mock-body">
            {settings.eyebrow && <small>{settings.eyebrow}</small>}
            <h3>{settings.title || 'Nadpis'}</h3>
            <p>{settings.text}</p>
            <div className="mt-mock-bars"><span /><span /><span /></div>
            {(settings.showContacts || settings.showStores) && <div className="mt-mock-cards">{settings.showContacts && <div>Kontakt</div>}{settings.showStores && <div>Naše cukrárne</div>}</div>}
            {settings.showForm && <div className="mt-mock-form"><b>Nechajte nám odkaz</b><span /><span /><span className="tall" /><em>Odoslať odkaz →</em></div>}
          </div>
        </div>
      </section>
    </div>

    <section className="panel">
      <div className="pa-head"><div><h2>Odkazy od zákazníkov {unread > 0 && <span className="mt-unread">{unread} nové</span>}</h2><p>Správy z formulára na stránke údržby. Kópia ide e-mailom na {settings.recipient}.</p></div></div>
      {!messages.length ? <div className="pa-empty"><span>✉</span><strong>Zatiaľ žiadne odkazy</strong><small>Keď vám zákazník napíše, odkaz sa zobrazí tu.</small></div> :
        <div className="mt-messages">{messages.map(m => <article key={m.id} className={`mt-msg ${m.readAt ? '' : 'is-new'} ${open === m.id ? 'is-open' : ''}`}>
          <button type="button" className="mt-msg-head" onClick={() => expand(m)}>
            <span className="mt-avatar" aria-hidden="true">{m.name.trim().charAt(0).toUpperCase()}</span>
            <span className="mt-msg-who"><strong>{m.name}</strong><small>{m.email}{m.phone ? ` · ${m.phone}` : ''}</small></span>
            <span className="mt-msg-preview">{m.message}</span>
            <time>{when(m.createdAt)}</time>
          </button>
          {open === m.id && <div className="mt-msg-body">
            <p>{m.message}</p>
            <div className="mt-msg-actions">
              <a className="primary-btn" href={`mailto:${m.email}?subject=${encodeURIComponent('Re: váš odkaz – Cukráreň Emili')}`}>Odpovedať e-mailom</a>
              {m.phone && <a className="pa-ghost" href={`tel:${m.phone.replace(/[^+\d]/g, '')}`}>Zavolať</a>}
              <button type="button" className="pa-link" onClick={() => void markRead(m, false)}>Označiť ako neprečítané</button>
              <button type="button" className="pa-link sk-danger" onClick={() => void remove(m)}>Vymazať</button>
              {!m.emailed && <small className="mt-warn-inline">E-mailom neodoslané</small>}
            </div>
          </div>}
        </article>)}</div>}
    </section>
  </div>
}
